import { Box, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import moment from 'moment';

import { UnitType } from 'src/api/roomService/types';
import { RoomAvailabilityCard } from 'src/components/room-availability-card/room-availability-card';
import { routes } from 'src/constants/routes';

import { useRoomsSearchParams } from './hooks/use-rooms-search-params';
import { styles } from './styles';
import { RoomsResultsOverviewProps } from './types';

export const RoomsResultsOverview: FC<RoomsResultsOverviewProps> = ({ results }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Results are fetched with the applied search, so link with the same values
    const { developmentSlug, academicYear, moveInDate, duration } = useRoomsSearchParams();
    const [selectedRoomTypes, setSelectedRoomTypes] = useState<Record<string, string>>({});

    const handleRoomTypeChange = (parentType: string, unitType: string) => {
        setSelectedRoomTypes((current) => ({
            ...current,
            [parentType]: unitType,
        }));
    };

    const handleViewRoom = (unitType: UnitType) => {
        if (!developmentSlug || !academicYear || !moveInDate || !duration) return;

        navigate({
            pathname: routes.rooms.viewRoom,
            search: createSearchParams({
                development: developmentSlug,
                roomCode: unitType.room.roomCode,
                academicYear,
                moveInDate: moment(moveInDate, 'YYYY-MM-DD').format('DD-MM-YYYY'),
                duration,
            }).toString(),
        });
    };

    return (
        <Box css={styles.resultsSection}>
            <Box css={styles.resultsHeader}>
                <Typography variant="h2" css={styles.resultsTitle}>
                    {t('rooms_page_room_types')}
                </Typography>
            </Box>

            <Box css={styles.resultsContainer}>
                {results.map((roomType, index) => (
                    <RoomAvailabilityCard
                        key={roomType.parentType}
                        title={roomType.parentType}
                        roomTypes={roomType.unitTypes}
                        selectedRoomType={selectedRoomTypes[roomType.parentType]}
                        onRoomTypeChange={(unitType) =>
                            handleRoomTypeChange(roomType.parentType, unitType)
                        }
                        onViewRoom={handleViewRoom}
                        imagePriority={index === 0 ? 'high' : 'low'}
                    />
                ))}
            </Box>

            <Typography css={styles.disclaimer}>{t('rooms_page_room_disclaimer')}</Typography>
        </Box>
    );
};
